import React, { useState, useRef } from 'react';
import { DB, uploadImage } from '../data/database';
import { CheckIcon } from './Icons';
import { logActivity } from '../data/notifications';

const BL_STATUSES = [
  { id: 'pending',              label: 'Pending',             color: 'var(--text3)' },
  { id: 'draft_received',      label: 'Draft Received',      color: 'var(--yellow)' },
  { id: 'corrections_sent',    label: 'Corrections Sent',    color: 'var(--orange)' },
  { id: 'revised_received',    label: 'Revised Received',    color: 'var(--blue)' },
  { id: 'finalized',           label: 'Finalized',           color: 'var(--green)' },
  { id: 'surrender_requested', label: 'Surrender Requested', color: 'var(--purple)' },
  { id: 'surrendered',         label: 'Surrendered',         color: 'var(--green)' },
];

function ContainerBL({ container, user, isCustomer, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [correctionNotes, setCorrectionNotes] = useState('');
  const [showCorrections, setShowCorrections] = useState(false);
  const fileRef = useRef();
  const [uploadType, setUploadType] = useState('draft');

  const blStatus = container.blStatus || 'pending';
  const blDocs = container.blDocuments || [];
  const statusInfo = BL_STATUSES.find(s => s.id === blStatus) || BL_STATUSES[0];

  const showStatuses = ['sealed', 'shipped', 'in_transit', 'at_port', 'customs', 'delivered', 'closed'];
  if (!showStatuses.includes(container.status)) return null;

  // Get customer IDs from units in this container
  const getCustomerIds = () => {
    const allUnits = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];
    const unitIds = container.units || [];
    return [...new Set(unitIds.map(uid => allUnits.find(u => u.id === uid)?.assignedTo).filter(Boolean))];
  };

  const save = (updates) => {
    const updated = { ...container, ...updates };
    const containers = DB.get('containers') || [];
    DB.set('containers', containers.map(c => c.id === container.id ? updated : c));

    // Fire BL notifications if status changed
    if (updates.blStatus && updates.blStatus !== blStatus) {
      logActivity.blStatusChanged?.(container.number || container.id, updates.blStatus, getCustomerIds());
    }

    onUpdate(updated);
  };

  const compressAndUpload = async (file) => {
    if (!file) return '';
    setUploading(true);
    try {
      if (file.type.startsWith('image/')) {
        const compressed = await new Promise(resolve => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
              const MAX = 1400;
              let { width, height } = img;
              if (width > MAX || height > MAX) {
                if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
                else { width = Math.round(width * MAX / height); height = MAX; }
              }
              const canvas = document.createElement('canvas');
              canvas.width = width; canvas.height = height;
              canvas.getContext('2d').drawImage(img, 0, 0, width, height);
              resolve(canvas.toDataURL('image/jpeg', 0.8));
            };
            img.src = e.target.result;
          };
          reader.readAsDataURL(file);
        });
        const url = await uploadImage(compressed);
        setUploading(false);
        return url;
      } else {
        // PDF or other — read as base64 and upload
        const dataUrl = await new Promise(resolve => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(file);
        });
        const url = await uploadImage(dataUrl);
        setUploading(false);
        return url;
      }
    } catch {
      setUploading(false);
      return '';
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const url = await compressAndUpload(file);
    if (!url) return;

    const doc = {
      type: uploadType,
      url,
      filename: file.name,
      uploaded_at: new Date().toISOString(),
      uploaded_by: user.id,
      uploaded_by_name: user.name,
      notes: '',
    };

    const newStatus = uploadType === 'draft' ? 'draft_received' : uploadType === 'revised' ? 'revised_received' : blStatus;
    save({ blDocuments: [...blDocs, doc], blStatus: newStatus });
  };

  const triggerUpload = (type) => {
    setUploadType(type);
    setTimeout(() => fileRef.current?.click(), 50);
  };

  const deleteDoc = (index) => {
    if (!window.confirm('Remove this document?')) return;
    save({ blDocuments: blDocs.filter((_, i) => i !== index) });
  };

  const sendCorrections = () => {
    if (!correctionNotes.trim()) return;
    const doc = {
      type: 'corrections',
      url: '',
      filename: '',
      uploaded_at: new Date().toISOString(),
      uploaded_by: user.id,
      uploaded_by_name: user.name,
      notes: correctionNotes.trim(),
    };
    save({ blDocuments: [...blDocs, doc], blStatus: 'corrections_sent' });
    setCorrectionNotes('');
    setShowCorrections(false);
  };

  const checkInvoiceStatus = () => {
    const invoices = DB.get('invoices') || [];
    const unitIds = container.units || [];
    const allUnitsData = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];
    const custIds = [...new Set(unitIds.map(uid => allUnitsData.find(u => u.id === uid)?.assignedTo).filter(Boolean))];
    const relevant = invoices.filter(i => custIds.includes(i.customerId) && (i.containerId === container.id || i.containerNumber === container.number || (i.lineItems || []).some(li => unitIds.includes(li.unit_id))));
    if (relevant.length === 0) return { paid: true, unpaid: [] };
    const unpaid = relevant.filter(i => i.status !== 'paid').map(i => ({ customer: i.customerName, amount: (i.total || 0) - (i.amountPaid || 0), inv: i.invoiceNumber }));
    return { paid: unpaid.length === 0, unpaid };
  };
  const invoiceCheck = checkInvoiceStatus();
  const allInvoicesPaid = () => invoiceCheck.paid;

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  const isImage = (url) => url && (url.includes('image') || url.match(/\.(jpg|jpeg|png|gif|webp)/i) || url.startsWith('data:image'));

  // Hidden file input
  const fileInput = <input ref={fileRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={handleFileSelected} />;

  // ── Customer view ──
  if (isCustomer) {
    const hasDraft = blDocs.some(d => d.type === 'draft' || d.type === 'revised');
    const isSurrendered = blStatus === 'surrendered';
    const latestBL = [...blDocs].reverse().find(d => d.type === 'draft' || d.type === 'revised');

    // Check if THIS customer's invoices for this container are paid
    const myInvoices = (DB.get('invoices') || []).filter(i => i.customerId === user?.id && (i.containerId === container.id || i.containerNumber === container.number || (i.lineItems || []).some(li => (container.units || []).includes(li.unit_id))));
    const myFullyPaid = myInvoices.length > 0 && myInvoices.every(i => i.status === 'paid');
    const myOutstanding = myInvoices.reduce((s, i) => s + ((i.total || 0) - (i.amountPaid || 0)), 0);

    return (
      <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Bill of Lading</div>
          <span style={{ fontSize: 10, fontWeight: 700, color: statusInfo.color }}>{statusInfo.label}</span>
        </div>

        {/* Not paid yet */}
        {!myFullyPaid && myOutstanding > 0 && (
          <div style={{ padding: '10px 14px', background: 'rgba(210,153,34,0.08)', border: '1px solid rgba(210,153,34,0.2)', borderRadius: 8, fontSize: 12, color: 'var(--yellow)', marginBottom: 8 }}>
            Complete payment (${myOutstanding.toLocaleString()} outstanding) to receive BL.
          </div>
        )}

        {/* Paid but BL not surrendered */}
        {myFullyPaid && !isSurrendered && (
          <div style={{ padding: '10px 14px', background: 'rgba(88,166,255,0.08)', borderRadius: 8, fontSize: 12, color: 'var(--blue)', marginBottom: 8 }}>
            ✅ Payment complete — BL surrender in progress.
          </div>
        )}

        {!hasDraft && !isSurrendered && <div style={{ fontSize: 12, color: 'var(--text3)', padding: '10px 0' }}>BL not yet available.</div>}
        {hasDraft && !isSurrendered && (
          <div style={{ padding: '10px 14px', background: 'rgba(88,166,255,0.08)', borderRadius: 8, fontSize: 12 }}>
            <div style={{ fontWeight: 600, color: 'var(--blue)', marginBottom: 4 }}>Draft BL Available</div>
            {latestBL?.url && <a href={latestBL.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cyan)' }}>⬇ Download Draft BL</a>}
          </div>
        )}
        {isSurrendered && latestBL?.url && (
          <div style={{ padding: '10px 14px', background: 'rgba(63,185,80,0.08)', borderRadius: 8, fontSize: 12 }}>
            <div style={{ fontWeight: 600, color: 'var(--green)', marginBottom: 4 }}>✅ Final BL Released</div>
            <a href={latestBL.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cyan)' }}>⬇ Download Final BL</a>
          </div>
        )}
      </div>
    );
  }

  // ── Admin view ──
  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
      {fileInput}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Bill of Lading</div>
        <span style={{ fontSize: 11, fontWeight: 700, color: statusInfo.color, padding: '2px 10px', background: 'var(--bg3)', borderRadius: 10 }}>{statusInfo.label}</span>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {(blStatus === 'pending' || blStatus === 'corrections_sent') && (
          <button className="btn btn-secondary btn-sm" onClick={() => triggerUpload(blStatus === 'corrections_sent' ? 'revised' : 'draft')}>
            📄 {blStatus === 'corrections_sent' ? 'Upload Revised BL' : 'Upload Draft BL'}
          </button>
        )}
        {blStatus === 'draft_received' && (
          <button className="btn btn-secondary btn-sm" onClick={() => setShowCorrections(true)}>Send Corrections</button>
        )}
        {blStatus === 'corrections_sent' && (
          <button className="btn btn-secondary btn-sm" onClick={() => triggerUpload('revised')}>📄 Upload Revised BL</button>
        )}
        {(blStatus === 'draft_received' || blStatus === 'revised_received') && (
          <button className="btn btn-success btn-sm" onClick={() => save({ blStatus: 'finalized' })}><CheckIcon />Finalize BL</button>
        )}
        {blStatus === 'finalized' && (
          <button className="btn btn-primary btn-sm" disabled={!allInvoicesPaid()}
            title={allInvoicesPaid() ? '' : 'All invoices must be paid'} onClick={() => save({ blStatus: 'surrender_requested' })}>
            Request Surrender
          </button>
        )}
        {blStatus === 'finalized' && !allInvoicesPaid() && (
          <div style={{ fontSize: 10, color: 'var(--red)', alignSelf: 'center' }}>
            Cannot surrender — outstanding:
            {invoiceCheck.unpaid.map((u, i) => <div key={i}>{u.customer}: ${u.amount.toLocaleString()} ({u.inv})</div>)}
          </div>
        )}
        {blStatus === 'surrender_requested' && (
          <button className="btn btn-success btn-sm" onClick={() => save({ blStatus: 'surrendered' })}><CheckIcon />Mark Surrendered</button>
        )}
      </div>

      {uploading && <div style={{ fontSize: 12, color: 'var(--blue)', marginBottom: 10 }}>Uploading document...</div>}

      {/* Corrections form */}
      {showCorrections && (
        <div style={{ padding: 12, background: 'var(--bg3)', borderRadius: 8, marginBottom: 12 }}>
          <label className="form-label">Correction Notes</label>
          <textarea className="form-input" rows={3} value={correctionNotes} onChange={e => setCorrectionNotes(e.target.value)}
            placeholder="Describe corrections needed" style={{ resize: 'vertical', marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowCorrections(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={!correctionNotes.trim()} onClick={sendCorrections}>Send</button>
          </div>
        </div>
      )}

      {/* Document history */}
      {blDocs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[...blDocs].reverse().map((doc, reverseIdx) => {
            const realIdx = blDocs.length - 1 - reverseIdx;
            return (
              <div key={realIdx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--bg3)', borderRadius: 8 }}>
                {/* Thumbnail or icon */}
                {doc.url ? (
                  isImage(doc.url) ? (
                    <img src={doc.url} alt="" onClick={() => window.open(doc.url, '_blank')}
                      style={{ width: 48, height: 36, borderRadius: 4, objectFit: 'cover', cursor: 'pointer', flexShrink: 0 }} />
                  ) : (
                    <div onClick={() => window.open(doc.url, '_blank')}
                      style={{ width: 48, height: 36, borderRadius: 4, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, fontSize: 18 }}>
                      📄
                    </div>
                  )
                ) : (
                  <div style={{ width: 48, height: 36, borderRadius: 4, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 14 }}>
                    📝
                  </div>
                )}

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>
                    {doc.type.replace(/_/g, ' ')}
                    {doc.filename && <span style={{ fontWeight: 400, color: 'var(--text3)', marginLeft: 6 }}>{doc.filename}</span>}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                    {formatDate(doc.uploaded_at)}
                    {doc.uploaded_by_name && ` · by ${doc.uploaded_by_name}`}
                  </div>
                  {doc.notes && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{doc.notes}</div>}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {doc.url && (
                    <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: 'var(--cyan)', textDecoration: 'none' }}>⬇</a>
                  )}
                  <button onClick={() => deleteDoc(realIdx)}
                    style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(248,81,73,0.15)', border: 'none', color: 'var(--red)', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ContainerBL;
